/**
 * Small concurrency guards shared by the runtime and the camera hook. No DOM, no
 * config — testable under plain tsx.
 */

/**
 * Single-flight guard with a truthful "anything still running?" answer.
 *
 * The Watcher used to set `watcherLooking = false` in every request's `finally`,
 * so with two overlapping requests the eye closed while a frame was still
 * uploading. Here only the LAST request to leave reports idle, and `reset()`
 * (teardown) invalidates leavers from a previous session so a late `finally`
 * cannot touch the next session's flag.
 */
export class FlightGuard {
  private n = 0;
  private epoch = 0;

  get busy(): boolean {
    return this.n > 0;
  }

  get inFlight(): number {
    return this.n;
  }

  /**
   * Begin a request unless one is already running. Returns `null` when busy
   * (skip this tick), otherwise a `leave()` that reports whether the guard is
   * now idle and the caller is still current.
   */
  tryEnter(): (() => boolean) | null {
    if (this.n > 0) return null;
    return this.enter();
  }

  /** Begin a request regardless; `leave()` reports idle only for the last one. */
  enter(): () => boolean {
    this.n++;
    const mine = this.epoch;
    let left = false;
    return () => {
      if (left || mine !== this.epoch) return false;
      left = true;
      this.n = Math.max(0, this.n - 1);
      return this.n === 0;
    };
  }

  /** Forget everything in flight. Returns whether anything was. */
  reset(): boolean {
    const was = this.n > 0;
    this.epoch++;
    this.n = 0;
    return was;
  }
}

/**
 * Latest-wins ticket counter. `begin()` invalidates every earlier ticket, so a
 * stale async completion can detect it lost and clean up after itself (e.g.
 * stop the MediaStream it just opened) instead of overwriting the winner.
 */
export class Latest {
  private gen = 0;

  begin(): number {
    return ++this.gen;
  }

  isCurrent(ticket: number): boolean {
    return ticket === this.gen;
  }

  /** Invalidate all outstanding tickets without issuing a new one. */
  invalidate(): void {
    this.gen++;
  }
}
